// The element picker: names the element a reporter clicks by a CSS selector
// that finds it, and an excerpt of its text.
import type { PickedElement } from './report';

/** Longest text excerpt, in characters. */
export const MAX_TEXT = 200;
/** Most ancestors a selector walks up through. */
const MAX_DEPTH = 6;

/** An ID or class that a build or a framework made up, which names nothing stable. */
const GENERATED = /^(svelte-|s-)|^[a-z]?[0-9a-f]{6,}$|[0-9]{4,}/i;

function cssEscape(s: string): string {
  return typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(s) : s.replace(/[^a-zA-Z0-9_-]/g, (c) => '\\' + c);
}

/** One step of a selector: the tag, its stable classes, and its place among siblings of its kind when needed. */
function step(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const classes = [...el.classList].filter((c) => !GENERATED.test(c)).slice(0, 2);
  let s = tag + classes.map((c) => '.' + cssEscape(c)).join('');
  const parent = el.parentElement;
  if (parent) {
    const same = [...parent.children].filter((c) => c.tagName === el.tagName);
    if (same.length > 1 && parent.querySelectorAll(`:scope > ${s}`).length > 1) s += `:nth-of-type(${same.indexOf(el) + 1})`;
  }
  return s;
}

/**
 * A selector that finds `el` in its document: its ID when that is stable and
 * unique, otherwise a path of steps from the nearest ancestor that makes it
 * unique (at most a few levels up).
 */
export function selectorFor(el: Element): string {
  const doc = el.ownerDocument;
  const unique = (sel: string) => {
    try {
      const found = doc.querySelectorAll(sel);
      return found.length === 1 && found[0] === el;
    } catch {
      return false;
    }
  };
  if (el.id && !GENERATED.test(el.id) && unique('#' + cssEscape(el.id))) return '#' + cssEscape(el.id);
  const parts: string[] = [];
  let cur: Element | null = el;
  for (let depth = 0; cur && cur !== doc.documentElement && depth < MAX_DEPTH; depth++) {
    if (depth > 0 && cur.id && !GENERATED.test(cur.id)) {
      parts.unshift('#' + cssEscape(cur.id));
      break;
    }
    parts.unshift(step(cur));
    const sel = parts.join(' > ');
    if (unique(sel)) return sel;
    cur = cur.parentElement;
  }
  return parts.join(' > ');
}

/** The text a reader sees in `el`, collapsed, without what is typed into its fields. */
function visibleText(el: Element): string {
  const clone = el.cloneNode(true) as Element;
  for (const f of clone.querySelectorAll('input, textarea, select, [contenteditable]')) f.remove();
  const text = (clone as HTMLElement).innerText ?? clone.textContent ?? '';
  return text.replace(/\s+/g, ' ').trim();
}

/** `el` as a report names it. */
export function describeElement(el: Element): PickedElement {
  const text = visibleText(el);
  return { selector: selectorFor(el), text: text.length > MAX_TEXT ? text.slice(0, MAX_TEXT - 1) + '…' : text };
}
